import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { styled } from '@mui/material/styles';

import type { ViewName } from './ViewToggle';
import { commits, startsOnOwnDrag, useSwipeGesture } from '../shared/useSwipeGesture';

/**
 * Explore and My Schedule as two pages side by side, below 900px.
 *
 * My Schedule sits immediately to the right of Explore. A swipe left on the
 * events list drags both pages together until My Schedule is in view; a swipe
 * right on My Schedule, anywhere but its week and day views, drags back. Tapping
 * the header's toggle plays the same move. Releasing past a share of the width,
 * or with a flick, finishes it; less springs back.
 *
 * At rest the strip is parked by margin, not transform, so nothing inside gains
 * a new containing block and every sticky header behaves as it did. The
 * transform exists only while the pages are moving, and a drag writes it
 * straight to the element rather than rendering.
 */

const MOVE_MS = 420;
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

const Viewport = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  // clip, not hidden: hidden makes a scroll container and unpins sticky headers.
  overflowX: 'clip',
});

const Strip = styled('div')({
  display: 'flex',
  width: '100%',
  minHeight: 0,
});

const Page = styled('div')({
  flex: '0 0 100%',
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  // The pages own the horizontal axis; vertical stays each page's scroll.
  touchAction: 'pan-y',
});

export interface PageSwipeHandle {
  /** Follow a swipe left begun on Explore's own surface: dx since it began. */
  drag: (dx: number) => void;
  release: (dx: number, velocity: number) => void;
  cancel: () => void;
}

interface Props {
  ref?: Ref<PageSwipeHandle>;
  view: ViewName;
  onView: (view: ViewName) => void;
  explore: ReactNode;
  /** Null until it is first needed, so arriving on Explore doesn't also build My Schedule. */
  schedule: ReactNode;
  /** Explore's own swipes, which App shares between these pages and the filter panel. */
  onExplorePointerDown: (e: ReactPointerEvent) => void;
  /** Swipes on My Schedule: touch screens, and a phone's mouse. */
  accepts: (e: ReactPointerEvent) => boolean;
  /** Explore's offset from rest as the pages move, for the filter tab to travel with it. */
  onExploreOffset: (px: number, ms: number, ease: string) => void;
}

export default function PageSwipe({
  ref,
  view,
  onView,
  explore,
  schedule,
  onExplorePointerDown,
  accepts,
  onExploreOffset,
}: Props) {
  const viewport = useRef<HTMLDivElement | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  /** Set while a finished move hands over to `view`, which is about to change to match. */
  const handingOver = useRef(false);
  const moving = useRef(false);
  const viewNow = useRef(view);
  const shown = useRef(view);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const width = () => viewport.current?.clientWidth || window.innerWidth;

  /** Explore's resting offset for a view: in place, or a page to the left. */
  const restOf = useCallback((v: ViewName) => (v === 'schedule' ? -width() : 0), []);

  /** Move the strip `px` from where the current view rests. */
  const paint = useCallback(
    (px: number, ms = 0, ease = EASE_OUT) => {
      const el = strip.current;
      if (el) {
        el.style.transition = ms ? `transform ${ms}ms ${ease}` : 'none';
        el.style.transform = px ? `translateX(${px}px)` : '';
      }
      onExploreOffset(restOf(shown.current) + px, ms, ease);
    },
    [onExploreOffset, restOf],
  );

  /** Animate to `to` px from the current rest, then hand over to `next`, if any. */
  const settle = useCallback(
    (to: number, next: ViewName | null, ms: number, ease: string) => {
      moving.current = true;
      paint(to, ms, ease);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        moving.current = false;
        if (next && next !== viewNow.current) {
          handingOver.current = true;
          onView(next);
        } else {
          paint(0);
        }
      }, ms);
    },
    [onView, paint],
  );

  /**
   * A view change: either the hand-over at the end of a finished move, which
   * only needs the transform cleared as the margin takes over, or a tap on the
   * toggle, which plays the move from the page that was showing.
   */
  useLayoutEffect(() => {
    viewNow.current = view;
    const previous = shown.current;
    shown.current = view;
    if (previous === view) return;
    if (handingOver.current) {
      handingOver.current = false;
      paint(0);
      return;
    }
    // The margin has already jumped; start the strip back where it was.
    const from = restOf(previous) - restOf(view);
    paint(from);
    strip.current?.getBoundingClientRect();
    settle(0, null, MOVE_MS, EASE_IN_OUT);
  }, [view, paint, restOf, settle]);

  // Keep the tab off screen at the new width when My Schedule is showing.
  useEffect(() => {
    const onResize = () => {
      if (!moving.current) onExploreOffset(restOf(shown.current), 0, EASE_OUT);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onExploreOffset, restOf]);

  /** A drag from Explore toward My Schedule, or back: never past either page. */
  const follow = useCallback(
    (dx: number) => {
      const w = width();
      paint(shown.current === 'explore' ? Math.max(-w, Math.min(0, dx)) : Math.min(w, Math.max(0, dx)));
    },
    [paint],
  );

  const release = useCallback(
    (dx: number, velocity: number) => {
      const w = width();
      const sign = shown.current === 'explore' ? -1 : 1;
      const done = commits(dx, velocity, w, sign);
      const remaining = done ? w - Math.abs(dx) : Math.abs(dx);
      const ms = Math.max(180, Math.round(MOVE_MS * (remaining / w)));
      if (done) settle(sign * w, shown.current === 'explore' ? 'schedule' : 'explore', ms, EASE_OUT);
      else settle(0, null, ms, EASE_OUT);
    },
    [settle],
  );

  useImperativeHandle(
    ref,
    () => ({
      drag: follow,
      release,
      cancel: () => settle(0, null, 220, EASE_OUT),
    }),
    [follow, release, settle],
  );

  const onScheduleSwipe = useSwipeGesture({
    accepts: (e) => !moving.current && shown.current === 'schedule' && accepts(e) && !startsOnOwnDrag(e.target),
    claim: (direction) => direction === 1,
    move: follow,
    release,
    cancel: () => settle(0, null, 220, EASE_OUT),
  });

  return (
    <Viewport ref={viewport}>
      <Strip ref={strip} style={{ marginLeft: view === 'schedule' ? '-100%' : 0 }}>
        <Page inert={view !== 'explore'} onPointerDown={onExplorePointerDown}>
          {explore}
        </Page>
        <Page inert={view !== 'schedule'} onPointerDown={onScheduleSwipe}>
          {schedule}
        </Page>
      </Strip>
    </Viewport>
  );
}
